import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useT } from '../i18n';
import { likeDesigner, unlikeDesigner, isLiked } from '../firebase/likeService';
import { patchDesignerLikeCount } from '../firebase/designerService';
import { patchShopLikeCount } from '../firebase/shopService';
import './LikeButton.css';

// ── LIKE ─────────────────────────────────────────────────────────────────────
// Split into a hook and two views because the count and the button sit in
// DIFFERENT parts of the profile header — the count under the name, the button
// under the contacts — while sharing one piece of live state. One component
// rendering both could not be in two places at once.
//
// The COUNT itself comes from the designer's document, maintained by a
// Firestore trigger; nothing here ever writes it, only the like itself.

// Owns the state. The count is adjusted OPTIMISTICALLY so the number moves the
// instant you click: the trigger's write lands a moment later and the next page
// load shows the authoritative value. Without it the button looks broken for the
// second or so the round trip takes.
// `onAnonymous` — what happens when someone without an account presses it. The
// default sends them to /login, which is right for a button reached from inside
// the app; a public profile passes its own, opening the sign-up dialog over the
// page rather than throwing that page away. See GuestGateModal.
export function useLike(designerUid, initialCount = 0, { onAnonymous } = {}) {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [liked, setLiked] = useState(false);
  const [count, setCount] = useState(initialCount);
  const [busy, setBusy] = useState(false);

  // The profile loads after this hook first runs, so the real count arrives late.
  useEffect(() => { setCount(initialCount); }, [initialCount]);

  useEffect(() => {
    let alive = true;
    if (!user?.uid || !designerUid) { setLiked(false); return undefined; }
    isLiked(user.uid, designerUid).then(v => { if (alive) setLiked(v); }).catch(() => {});
    return () => { alive = false; };
  }, [user?.uid, designerUid]);

  const isOwn = !!user?.uid && user.uid === designerUid;

  const toggle = useCallback(async () => {
    // Liking requires an identity — the like document is keyed on the liker.
    if (!user?.uid) {
      if (onAnonymous) onAnonymous(); else navigate('/login');
      return;
    }
    if (isOwn || busy) return;

    const next = !liked;
    setLiked(next);
    setCount(c => Math.max(0, c + (next ? 1 : -1)));
    setBusy(true);
    try {
      if (next) await likeDesigner(user.uid, designerUid);
      else await unlikeDesigner(user.uid, designerUid);
      // Keep the cached directories in step. The feed and the search page
      // render from those caches for up to five minutes, so without this a like
      // made here is invisible the moment you navigate back to where you came
      // from. BOTH are patched: a uid is in exactly one of them, and each call
      // is a no-op for a row it does not hold — cheaper and simpler than
      // threading the liked account's role down to this hook.
      patchDesignerLikeCount(designerUid, next ? 1 : -1);
      patchShopLikeCount(designerUid, next ? 1 : -1);
    } catch {
      // Put it back — the server rejected it.
      setLiked(!next);
      setCount(c => Math.max(0, c + (next ? -1 : 1)));
    } finally {
      setBusy(false);
    }
  }, [user?.uid, designerUid, liked, busy, isOwn, navigate, onAnonymous]);

  return { liked, count, busy, isOwn, toggle };
}

function Heart({ filled, size = 17 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24"
      fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round">
      <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1-1.1a5.5 5.5 0 0 0-7.8 7.8l1 1.1L12 21.2l7.8-7.7 1-1.1a5.5 5.5 0 0 0 0-7.8z" />
    </svg>
  );
}

// How many people liked this designer. Read-only — pressing is the button's job.
// Sits under the name at every width, well away from the button, which is why
// the state had to move up into the hook.
export function LikeCount({ count }) {
  const { t } = useT();
  return (
    <span className="lb-count" title={t('like_count')}>
      <span className="lb-count-label">{t('like_count')}</span>
      <span className="lb-count-n">{count}</span>
    </span>
  );
}

export default function LikeButton({ like }) {
  const { t } = useT();
  const { liked, busy, isOwn, toggle } = like;

  return (
    <div className="lb-wrap">
      <button
        type="button"
        className={`lb${liked ? ' lb--on' : ''}${isOwn ? ' lb--own' : ''}`}
        onClick={toggle}
        disabled={busy || isOwn}
        // Your own profile still SHOWS the count; it just can't be liked.
        title={isOwn ? t('like_own') : liked ? t('like_remove') : t('like_add')}
        aria-pressed={liked}
      >
        <Heart filled={liked} size={19} />
        <span className="lb-label">{liked ? t('like_liked') : t('like_add')}</span>
      </button>
    </div>
  );
}
